import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RcService } from './rc.service';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    private isRedirecting = false;
    private redirectQueue: (() => void)[] = [];

    constructor(
        private readonly router: Router,
        private readonly rcService: RcService,
        private readonly authService: AuthService
    ) { }

    /**
     * Safely navigate to a route without causing redirect loops
     */
    safeNavigate(url: string[], options: { replaceUrl?: boolean } = {}): Promise<boolean> {
        if (this.isRedirecting) {
            // Queue the navigation if we're already redirecting
            return new Promise((resolve) => {
                this.redirectQueue.push(() => {
                    this.router.navigate(url, { replaceUrl: true, ...options }).then(resolve);
                });
            });
        }

        this.isRedirecting = true;

        return this.router.navigate(url, { replaceUrl: true, ...options }).finally(() => {
            this.isRedirecting = false;
            this.processRedirectQueue();
        });
    }

    /**
     * Process queued redirects
     */
    private processRedirectQueue(): void {
        if (this.redirectQueue.length > 0) {
            const nextRedirect = this.redirectQueue.shift();
            if (nextRedirect) {
                setTimeout(() => nextRedirect(), 0);
            }
        }
    }

    /**
     * Navigate to appropriate login page based on RC
     */
    navigateToLogin(rc?: string): Promise<boolean> {
        const isLocalhost = globalThis.location.hostname.includes('localhost') ||
            globalThis.location.hostname.includes('127.0.0.1');

        if (isLocalhost || globalThis.location.hostname === 'rms.katdev.com') {
            return this.safeNavigate(['/auth/login']);
        }

        if (rc && this.rcService.isValidRc(rc)) {
            this.rcService.setRc(rc);
            return this.safeNavigate([`/${rc}/auth/login`]);
        }

        return this.safeNavigate(['/select-rc']);
    }

    /**
     * Navigate to appropriate dashboard based on RC and auth status
     */
    navigateToDashboard(rc?: string): Promise<boolean> {
        const isLocalhost = globalThis.location.hostname.includes('localhost') ||
            globalThis.location.hostname.includes('127.0.0.1');

        if (isLocalhost || globalThis.location.hostname === 'rms.katdev.com') {
            return this.safeNavigate(['/']);
        }

        if (!this.authService.isAuthenticated()) {
            return this.navigateToLogin(rc);
        }

        if (rc && this.rcService.isValidRc(rc)) {
            this.rcService.setRc(rc);
            return this.safeNavigate([`/${rc}`]);
        }

        return this.safeNavigate(['/select-rc']);
    }

    /**
     * Navigate to RC selection page
     */
    navigateToRcSelection(): Promise<boolean> {
        return this.safeNavigate(['/select-rc']);
    }

    /**
     * Clear redirect queue (useful for cleanup)
     */
    clearRedirectQueue(): void {
        this.redirectQueue = [];
        this.isRedirecting = false;
    }
} 